import { useState, useCallback } from 'react';
import { getDataByDocument } from '../../../services/documents';

interface DocumentLookupResult {
  // Natural Person (CPF)
  name?: string;
  birthDate?: string;
  gender?: string;
  motherName?: string;

  // Legal Person (CNPJ)
  companyName?: string;
  foundationDate?: string;
  shareCapital?: string;
  phone?: string;
  email?: string;

  // Address (for CNPJ)
  zipCode?: string;
  street?: string;
  number?: string;
  district?: string;
  city?: string;
  state?: string;
}

interface UseDocumentLookupReturn {
  lookup: (document: string) => Promise<DocumentLookupResult | null>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export const useDocumentLookup = (): UseDocumentLookupReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const lookup = useCallback(async (document: string): Promise<DocumentLookupResult | null> => {
    // Remove non-numeric characters
    const cleanDocument = document.replace(/\D/g, '');

    // Validate document length
    if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getDataByDocument(cleanDocument);

      if (!response) {
        return null;
      }

      // CPF Response
      if (cleanDocument.length === 11) {
        return {
          name: response.nome || response.name || '',
          birthDate: response.data_nascimento || response.birthDate || '',
          gender: response.sexo || response.gender || '',
          motherName: response.nome_mae || response.motherName || '',
        };
      }

      // CNPJ Response
      if (cleanDocument.length === 14) {
        // Parse foundation date
        let foundationDate = '';
        if (response.abertura || response.data_abertura || response.foundationDate) {
          const rawDate = response.abertura || response.data_abertura || response.foundationDate;
          // Convert DD/MM/YYYY to YYYY-MM-DD if needed
          if (rawDate.includes('/')) {
            const [day, month, year] = rawDate.split('/');
            foundationDate = `${year}-${month}-${day}`;
          } else {
            foundationDate = rawDate;
          }
        }

        return {
          companyName: response.nome || response.razao_social || response.companyName || '',
          foundationDate,
          shareCapital: response.capital_social || response.shareCapital || '',
          phone: response.telefone || response.phone || '',
          email: response.email || '',
          zipCode: response.cep || '',
          street: response.logradouro || '',
          number: response.numero || '',
          district: response.bairro || '',
          city: response.municipio || response.cidade || '',
          state: response.uf || '',
        };
      }

      return null;
    } catch (err) {
      console.error('Document lookup error:', err);
      setError('Não foi possível consultar o documento. Tente novamente.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    lookup,
    isLoading,
    error,
    clearError,
  };
};

export default useDocumentLookup;
